// Read-state policy for channel lifecycle events. This is the single place that
// decides what "the viewer read this channel" means for each event; UnreadManager
// only wires React/DOM lifecycle into these functions.
//
// The unread system has exactly three layers:
//
//   1. VISUAL (session) — `unread_store.js`. An in-memory overlay cursor per channel
//      plus counts recomputed from Dexie. Advancing the overlay clears badges
//      instantly and never touches the network.
//   2. DURABLE — `read_cursor.js`. The `channel_read_markers` row + server
//      `mark_as_read`. Everything the client has locally seen, deduped and monotonic.
//   3. PRECISE TAIL — `feed/echo/readCursor.ts`. While a channel is open, the feed
//      advances the durable cursor to the exact rendered tail (channel-open,
//      bottom-reach, focus/blur), gated on focus and live-bottom visibility.
//
// Event → action matrix implemented below:
//
//   enter channel    visit snapshot (divider baseline) + overlay + durable advance
//   leave channel    overlay + durable advance (keepalive; SPA nav survives it)
//   window focus     overlay only (the feed sends the precise durable advance)
//   window blur      overlay only (reading stopped at this instant)
//   page teardown    markChannelFullyRead + durable advance, both keepalive —
//                    pagehide/unload can't await Dexie, so the bare POST guarantees
//                    something durable escapes the dying page
//
// Own messages never count as unread anywhere: the server auto-advances the author's
// cursor on send, and the store excludes own-authored and optimistic rows outright.

import { advanceReadCursor, markChannelFullyRead } from './read_cursor';
import { logDebug } from './logger';
import { beginChannelVisit, overlayMarkAsRead } from './unread_store';

export function enterChannel({ workspaceId, channelId, profileId }) {
  if (!workspaceId || !channelId) return;

  logDebug('unreads', 'lifecycle: enter channel', channelId);

  beginChannelVisit(channelId);
  overlayMarkAsRead(channelId);
  void advanceReadCursor(workspaceId, channelId, profileId, 'channel-enter');
}

export function leaveChannel({ workspaceId, channelId, profileId }) {
  if (!workspaceId || !channelId) return;

  logDebug('unreads', 'lifecycle: leave channel', channelId);

  overlayMarkAsRead(channelId);
  void advanceReadCursor(workspaceId, channelId, profileId, 'channel-leave', { keepalive: true });
}

export function focusChannel({ channelId }) {
  if (!channelId) return;

  logDebug('unreads', 'lifecycle: window focus', channelId);
  overlayMarkAsRead(channelId);
}

export function blurChannel({ channelId }) {
  if (!channelId) return;

  logDebug('unreads', 'lifecycle: window blur', channelId);
  overlayMarkAsRead(channelId);
}

export function teardownChannel({ workspaceId, channelId, profileId, via }) {
  if (!workspaceId || !channelId) return;

  logDebug('unreads', `lifecycle: teardown via ${via}`, channelId);
  // shadow/shared visitors have no profile and no server read state to advance
  if (profileId) markChannelFullyRead(channelId, via);
  void advanceReadCursor(workspaceId, channelId, profileId, via, { keepalive: true });
  overlayMarkAsRead(channelId);
}
